// src/pages/dashboard/NewsletterCompose.js
// Route: /dashboard/marketing/compose
// Sprint 7.3 — Compose, preview, and send practitioner newsletters to subscribers.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Send, Mail, Eye, EyeOff } from 'lucide-react';
import { auth, db } from '../../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot, collection, getDocs, where, query } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import LoadingButton from '../../components/common/LoadingButton';

// ─── Color scheme map (mirrors WebsiteRenderer) ───────────────────────────────

const SCHEME_COLORS = {
  'lavender-gold':  '#7C3AED',
  'sage-cream':     '#5C7F67',
  'ocean-sand':     '#0369A1',
  'rose-blush':     '#BE185D',
  'midnight-gold':  '#6366F1',
  'slate-silver':   '#475569',
  'emerald':        '#059669',
};

function resolveBrandColor(practitionerData) {
  const website = practitionerData?.website_published || practitionerData?.website_draft || {};
  const custom = website.design?.customColors?.primary;
  if (custom) return custom;
  const scheme = website.design?.colorScheme || 'lavender-gold';
  return SCHEME_COLORS[scheme] || '#7c3aed';
}

function resolvePractitionerName(practitionerData) {
  return (
    practitionerData?.website_published?.profile?.displayName ||
    practitionerData?.website_published?.hero?.headline ||
    practitionerData?.displayName ||
    'Your Practice'
  );
}

// ─── Live email preview (mirrors newsletter-email-template.js layout) ─────────

function EmailPreview({ subject, body, practitionerName, brandColor }) {
  const accent = brandColor || '#7c3aed';
  const name = practitionerName || 'Your Practice';

  const paragraphs = (body || '')
    .split(/\n\n+/)
    .map(p => p.trim())
    .filter(Boolean);

  return (
    <div style={{
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
      backgroundColor: '#F9FAFB',
      padding: '24px 16px',
      borderRadius: '8px',
      minHeight: '400px',
    }}>
      {/* Card */}
      <div style={{
        maxWidth: '520px',
        margin: '0 auto',
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 28px 0' }}>
          <p style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: accent }}>{name}</p>
        </div>

        {/* Divider */}
        <div style={{ padding: '12px 28px 0' }}>
          <div style={{ height: '1px', backgroundColor: '#E5E7EB' }} />
        </div>

        {/* Subject */}
        <div style={{ padding: '20px 28px 0' }}>
          <h1 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>
            {subject || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Your subject line will appear here</span>}
          </h1>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 28px 28px' }}>
          {paragraphs.length > 0 ? paragraphs.map((para, i) => (
            <p key={i} style={{ margin: '0 0 14px', fontSize: '14px', color: '#374151', lineHeight: 1.7 }}>
              {para.split('\n').map((line, j, arr) => (
                <React.Fragment key={j}>{line}{j < arr.length - 1 && <br />}</React.Fragment>
              ))}
            </p>
          )) : (
            <p style={{ margin: 0, fontSize: '14px', color: '#9CA3AF', fontStyle: 'italic' }}>
              Your newsletter content will appear here as you type...
            </p>
          )}
        </div>

        {/* Divider */}
        <div style={{ padding: '0 28px' }}>
          <div style={{ height: '1px', backgroundColor: '#E5E7EB' }} />
        </div>

        {/* Footer */}
        <div style={{ backgroundColor: '#F9FAFB', padding: '16px 28px', textAlign: 'center' }}>
          <p style={{ margin: '0 0 4px', fontSize: '11px', color: '#9CA3AF' }}>
            You received this email because you subscribed to {name}'s newsletter on Divine Frameworks.
          </p>
          <p style={{ margin: '0 0 4px', fontSize: '11px', color: '#9CA3AF' }}>
            <span style={{ color: accent, textDecoration: 'underline', cursor: 'default' }}>Unsubscribe</span>
            {' · '}
            <span style={{ color: accent, textDecoration: 'underline', cursor: 'default' }}>Privacy Policy</span>
          </p>
          <p style={{ margin: 0, fontSize: '11px', color: '#9CA3AF' }}>
            © {new Date().getFullYear()} Powered by{' '}
            <span style={{ color: accent, fontWeight: 600 }}>Divine Frameworks</span>
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Confirmation modal ────────────────────────────────────────────────────────

function ConfirmModal({ subject, recipientCount, onConfirm, onCancel, sending }) {
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
          <Send className="w-5 h-5 text-purple-600" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 text-center mb-2">Ready to send?</h3>
        <p className="text-sm text-gray-600 text-center mb-1">
          This newsletter will be sent to{' '}
          <span className="font-semibold text-gray-900">{recipientCount} active subscriber{recipientCount !== 1 ? 's' : ''}</span>.
        </p>
        <p className="text-sm text-gray-500 text-center mb-6">
          Subject: <span className="italic">"{subject}"</span>
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={sending}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-60 transition-colors"
          >
            Cancel
          </button>
          <LoadingButton
            onClick={onConfirm}
            loading={sending}
            loadingText="Sending…"
            className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
          >
            Send Now
          </LoadingButton>
        </div>
      </div>
    </div>
  );
}

// ─── Success banner ────────────────────────────────────────────────────────────

function SuccessBanner({ message, onDismiss }) {
  return (
    <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 flex items-start justify-between gap-3">
      <div className="flex items-start gap-3">
        <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="text-sm text-green-800 font-medium">{message}</p>
      </div>
      <button onClick={onDismiss} className="text-green-600 hover:text-green-800 text-lg leading-none flex-shrink-0">×</button>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

const NewsletterCompose = () => {
  const navigate = useNavigate();
  const [practitionerId, setPractitionerId] = useState(null);
  const [practitionerData, setPractitionerData] = useState(null);
  const [subscriberCount, setSubscriberCount] = useState(0);

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [showPreview, setShowPreview] = useState(true);

  const [testLoading, setTestLoading] = useState(false);
  const [testSuccess, setTestSuccess] = useState('');
  const [testError, setTestError] = useState('');

  const [showConfirm, setShowConfirm] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendSuccess, setSendSuccess] = useState('');
  const [sendError, setSendError] = useState('');

  // Auth — done flag + 3s timeout
  useEffect(() => {
    let done = false;
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user && !done) {
        done = true;
        setPractitionerId(user.uid);
      }
    });
    const timeout = setTimeout(() => { if (!done) { done = true; } }, 3000);
    return () => { unsub(); clearTimeout(timeout); };
  }, []);

  // Load practitioner doc
  useEffect(() => {
    if (!practitionerId) return;
    const ref = doc(db, 'practitioners', practitionerId);
    const unsub = onSnapshot(ref, (snap) => {
      setPractitionerData(snap.exists() ? snap.data() : {});
    });
    return () => unsub();
  }, [practitionerId]);

  // Count active subscribers
  useEffect(() => {
    if (!practitionerId) return;
    const ref = collection(db, 'practitioners', practitionerId, 'subscribers');
    const q = query(ref, where('status', '==', 'active'));
    getDocs(q).then(snap => setSubscriberCount(snap.size)).catch(() => {});
  }, [practitionerId]);

  const brandColor = practitionerData ? resolveBrandColor(practitionerData) : '#7c3aed';
  const practitionerName = practitionerData ? resolvePractitionerName(practitionerData) : '';
  const practitionerEmail = practitionerData?.email || auth?.currentUser?.email || '';

  const canSend = subject.trim().length > 0 && body.trim().length > 0;

  const callSendNewsletter = useCallback(async (sendTest) => {
    const functions = getFunctions(undefined, 'us-west1');
    const fn = httpsCallable(functions, 'sendNewsletter');
    return fn({ subject: subject.trim(), body: body.trim(), sendTest });
  }, [subject, body]);

  const handleSendTest = async () => {
    if (!canSend) return;
    setTestLoading(true);
    setTestSuccess('');
    setTestError('');
    try {
      await callSendNewsletter(true);
      setTestSuccess('Test email sent to your inbox! Check your email to preview how it looks.');
    } catch (err) {
      setTestError(err.message || 'Failed to send test email.');
    } finally {
      setTestLoading(false);
    }
  };

  const handleSendAll = async () => {
    if (!canSend) return;
    setSendLoading(true);
    setSendError('');
    try {
      const result = await callSendNewsletter(false);
      const count = result.data?.recipientCount ?? 0;
      setSendSuccess(
        count === 0
          ? 'Newsletter sent! (No active subscribers at this time.)'
          : `Newsletter sent to ${count} subscriber${count !== 1 ? 's' : ''}!`
      );
      setShowConfirm(false);
      // Reset form after successful send
      setSubject('');
      setBody('');
    } catch (err) {
      setSendError(err.message || 'Failed to send newsletter.');
      setShowConfirm(false);
    } finally {
      setSendLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">

      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-6 text-sm text-gray-500">
        <button
          onClick={() => navigate('/dashboard/marketing')}
          className="flex items-center gap-1 hover:text-purple-600 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Marketing
        </button>
        <span>/</span>
        <span className="text-gray-900 font-medium">Send Newsletter</span>
      </div>

      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Compose Newsletter</h1>
        <p className="text-sm text-gray-500">
          Sending to{' '}
          <span className="font-semibold text-gray-700">{subscriberCount} active subscriber{subscriberCount !== 1 ? 's' : ''}</span>
        </p>
      </div>

      {/* Success / error banners */}
      {sendSuccess && (
        <SuccessBanner message={sendSuccess} onDismiss={() => setSendSuccess('')} />
      )}
      {sendError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <p className="text-sm text-red-700">{sendError}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* ── Left: Compose ─────────────────────────────────────────── */}
        <div className="space-y-5">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">

            {/* Sender info (read-only) */}
            <div className="mb-5 pb-5 border-b border-gray-100">
              <div className="flex items-center gap-2 mb-1">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
                  style={{ backgroundColor: brandColor }}
                >
                  {(practitionerName.charAt(0) || '?').toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{practitionerName || '—'}</p>
                  <p className="text-xs text-gray-400">{practitionerEmail || '—'}</p>
                </div>
              </div>
            </div>

            {/* Subject */}
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">
                Subject
              </label>
              <input
                type="text"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder={`e.g., March Updates from ${practitionerName || 'your practice'}`}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent transition-all"
                maxLength={150}
              />
            </div>

            {/* Body */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">
                Message
              </label>
              <textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                placeholder="Write your newsletter here. Line breaks will be preserved in the email. Use a blank line between paragraphs."
                rows={14}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent transition-all resize-y"
                style={{ minHeight: '240px' }}
              />
              <p className="text-xs text-gray-400 mt-1">Line breaks will be preserved in the email.</p>
            </div>
          </div>

          {/* Test send */}
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-1">Send a test first</h3>
            <p className="text-xs text-gray-500 mb-3">
              See exactly how your newsletter will look in an inbox — sent to <span className="font-medium">{practitionerEmail || 'your email'}</span>.
            </p>
            {testSuccess && (
              <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-3">{testSuccess}</p>
            )}
            {testError && (
              <p className="text-xs text-red-600 mb-3">{testError}</p>
            )}
            <LoadingButton
              onClick={handleSendTest}
              loading={testLoading}
              disabled={!canSend}
              loadingText="Sending test…"
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-white hover:border-purple-300 hover:text-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Mail className="w-4 h-4" /> Send Test to Myself
            </LoadingButton>
          </div>

          {/* Send CTA */}
          <button
            onClick={() => setShowConfirm(true)}
            disabled={!canSend || sendLoading || !!sendSuccess}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-xl text-sm font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-4 h-4" />
            Send to All Subscribers ({subscriberCount})
          </button>
        </div>

        {/* ── Right: Preview ────────────────────────────────────────── */}
        <div>
          {/* Preview toggle on mobile */}
          <div className="flex items-center justify-between mb-3 lg:hidden">
            <span className="text-sm font-semibold text-gray-700">Email Preview</span>
            <button
              onClick={() => setShowPreview(v => !v)}
              className="flex items-center gap-1.5 text-sm text-purple-600 hover:text-purple-700 transition-colors"
            >
              {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {showPreview ? 'Hide preview' : 'Show preview'}
            </button>
          </div>

          <div className="hidden lg:block mb-3">
            <span className="text-sm font-semibold text-gray-700">Email Preview</span>
            <span className="text-xs text-gray-400 ml-2">Updates as you type</span>
          </div>

          {showPreview && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <EmailPreview
                subject={subject}
                body={body}
                practitionerName={practitionerName}
                brandColor={brandColor}
              />
            </div>
          )}
        </div>
      </div>

      {/* Confirmation modal */}
      {showConfirm && (
        <ConfirmModal
          subject={subject}
          recipientCount={subscriberCount}
          onConfirm={handleSendAll}
          onCancel={() => setShowConfirm(false)}
          sending={sendLoading}
        />
      )}
    </div>
  );
};

export default NewsletterCompose;
